/**
 * Ship Buoyancy Shader — CPU-Driven
 *
 * The vertex shader receives pre-computed ocean height, pitch, and roll
 * as uniforms. Buoyancy probes are evaluated on the CPU once per frame
 * (3 calls to sampleOceanHeight) instead of redundantly per vertex on the GPU.
 *
 * This is dramatically faster than the old approach where every vertex
 * re-evaluated the full 16-iteration exp(sin) wave function 3× (probes)
 * × 3 (triplanar) = 9 times — identical work duplicated across hundreds
 * of vertices per ship.
 */

import * as THREE from 'three'

// ─── Vertex Shader ───

export const shipVertexShader = /* glsl */ `
  uniform float uRadius;
  uniform float uShipTheta;
  uniform float uShipPhi;
  uniform float uShipHeading;
  uniform float uOceanHeight;
  uniform float uPitch;
  uniform float uRoll;
  uniform vec3 uCameraDir;
  uniform float uCameraAlt;

  attribute vec3 color;

  varying vec3 vColor;
  varying vec3 vNormalW;
  varying vec3 vCamRel;
  varying vec3 vRadialDir;

  void main() {
    // ── Ship sphere normal from theta/phi ──
    float sinPhi = sin(uShipPhi);
    float cosPhi = cos(uShipPhi);
    float sinTheta = sin(uShipTheta);
    float cosTheta = cos(uShipTheta);

    vec3 shipNormal = vec3(sinPhi * sinTheta, cosPhi, sinPhi * cosTheta);

    // ── Tangent basis ──
    vec3 east = vec3(cosTheta, 0.0, -sinTheta);
    vec3 north = vec3(cosPhi * sinTheta, -sinPhi, cosPhi * cosTheta);

    float cosH = cos(uShipHeading);
    float sinH = sin(uShipHeading);
    vec3 forward = -north * cosH + east * sinH;
    vec3 right = cross(forward, shipNormal);

    // ── Pre-computed pitch and roll (from CPU buoyancy probes) ──
    float cp = cos(uPitch), sp = sin(uPitch);
    mat3 pitchMat = mat3(
      1.0, 0.0, 0.0,
      0.0, cp, sp,
      0.0, -sp, cp
    );

    float cr = cos(uRoll), sr = sin(uRoll);
    mat3 rollMat = mat3(
      cr, sr, 0.0,
      -sr, cr, 0.0,
      0.0, 0.0, 1.0
    );

    mat3 tilt = rollMat * pitchMat;

    // ── Orientation matrix (right-handed) ──
    // col0 = right (starboard), col1 = normal (up), col2 = -forward (stern)
    mat3 orient = mat3(
      right.x, right.y, right.z,
      shipNormal.x, shipNormal.y, shipNormal.z,
      -forward.x, -forward.y, -forward.z
    );

    // Flip mesh Z so bow (+Z in geometry) aligns with forward direction
    vec3 localPos = vec3(position.x, position.y, -position.z);
    vec3 worldOffset = orient * (tilt * localPos);

    // Normal transform
    vec3 localNrm = vec3(normal.x, normal.y, -normal.z);
    vNormalW = normalize(orient * (tilt * localNrm));

    vColor = color;
    vRadialDir = shipNormal;

    // ── Camera-relative rendering (matches SphericalOcean.tsx precision) ──
    vec3 diff = shipNormal - uCameraDir;
    vec3 camRel = diff * uRadius
                + shipNormal * uOceanHeight
                - uCameraDir * uCameraAlt
                + worldOffset;

    vCamRel = camRel;

    mat3 viewRot = mat3(modelViewMatrix);
    gl_Position = projectionMatrix * vec4(viewRot * camRel, 1.0);
  }
`

// ─── Fragment Shader ───

export const shipFragmentShader = /* glsl */ `
  uniform vec3 uSunDirection;
  uniform float uSunHeight;

  varying vec3 vColor;
  varying vec3 vNormalW;
  varying vec3 vCamRel;
  varying vec3 vRadialDir;

  void main() {
    vec3 normal = gl_FrontFacing ? normalize(vNormalW) : -normalize(vNormalW);
    vec3 lightDir = normalize(uSunDirection);

    // Day/night (matches lighting.ts)
    float sunIntensity = smoothstep(-0.05, 0.3, uSunHeight);
    float sunsetFactor = smoothstep(-0.05, 0.1, uSunHeight) * (1.0 - smoothstep(0.1, 0.3, uSunHeight));

    vec3 sunColor = mix(vec3(1.0, 0.98, 0.95), vec3(1.0, 0.6, 0.3), sunsetFactor);
    sunColor *= sunIntensity;

    // Ambient
    vec3 ambient = vec3(0.06, 0.06, 0.10);

    // Hemisphere ambient (slight sky contribution from above)
    float hemi = dot(normal, vRadialDir) * 0.5 + 0.5;
    vec3 hemiColor = mix(vec3(0.03, 0.03, 0.05), vec3(0.08, 0.10, 0.15) * sunIntensity, hemi);

    // Diffuse
    float NdL = max(dot(normal, lightDir), 0.0);
    vec3 diffuse = vColor * (NdL * sunColor + ambient + hemiColor);

    // Specular (Blinn-Phong, subtle for wood/metal)
    // vCamRel = vertex - camera, so viewDir = -vCamRel (same as ocean shader)
    vec3 viewDir = normalize(-vCamRel);
    vec3 halfVec = normalize(lightDir + viewDir);
    float spec = pow(max(dot(normal, halfVec), 0.0), 32.0) * 0.15 * sunIntensity;

    // Emissive glow for lantern glass (bright warm vertices)
    float lum = dot(vColor, vec3(0.299, 0.587, 0.114));
    vec3 emissive = vColor * smoothstep(0.55, 0.75, lum) * 0.6;

    vec3 finalColor = diffuse + vec3(spec) + emissive;

    gl_FragColor = vec4(finalColor, 1.0);
  }
`

// ─── Material Factory ───

export function createShipMaterial(radius: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: shipVertexShader,
    fragmentShader: shipFragmentShader,
    uniforms: {
      uRadius: { value: radius },
      uShipTheta: { value: 0 },
      uShipPhi: { value: 1.0 },
      uShipHeading: { value: 0 },
      uOceanHeight: { value: 0 },
      uPitch: { value: 0 },
      uRoll: { value: 0 },
      uSunDirection: { value: new THREE.Vector3(0.5, 0.3, 0.8).normalize() },
      uSunHeight: { value: 0.3 },
      uCameraDir: { value: new THREE.Vector3(0, 1, 0) },
      uCameraAlt: { value: 0 },
    },
    side: THREE.DoubleSide,
  })
}
